import React from 'react';
import './UISectionWrapper.scss';

export interface UISectionWrapperProps {
  /**
   * Вміст секції
   */
  children: React.ReactNode;
  /**
   * HTML id атрибут
   */
  id?: string;
  /**
   * Варіант padding та gap
   */
  spacing?: 'tight' | 'compact' | 'default' | 'spacious';

  /**
   * Горизонтальне вирівнювання контенту
   */
  align?: 'left' | 'center' | 'right' | 'stretch';

  /**
   * Вертикальне вирівнювання контенту
   */
  justify?: 'top' | 'center' | 'bottom' | 'space-between';

  /**
   * Варіант тіні
   */
  shadow?: 'none' | 'default' | 'light' | 'medium' | 'strong';

  /**
   * Варіант фону
   */
  background?: 'white' | 'transparent' | 'light' | 'gray';

  /**
   * Варіант рамки
   */
  border?: 'none' | 'default' | 'primary';

  /**
   * Інтерактивні ефекти
   */
  interactive?: 'none' | 'hoverable' | 'clickable';

  /**
   * Варіант layout для форм
   */
  layout?: 'default' | 'form-horizontal' | 'form-vertical';

  /**
   * Варіант padding для form layout
   */
  formPadding?: 'none' | 'small' | 'medium' | 'large';

  /**
   * Ширина лейблів для horizontal form
   */
  labelWidth?: number | string;

  /**
   * Gap між лейблами та полями
   */
  formGap?: number | string;

  /**
   * Додатковий CSS клас
   */
  className?: string;

  /**
   * Обробник кліку (тільки для clickable варіанта)
   */
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;

  /**
   * HTML атрибути
   */
  [key: string]: any;
}

// Моковые данные для демонстрації
const mockProps: Partial<UISectionWrapperProps> = {

  spacing: 'default',
  align: 'center',
  justify: 'center',
  shadow: 'default',
  background: 'white',
  border: 'none',
  interactive: 'none',
  layout: 'default',
  formPadding: 'medium',
  labelWidth: '133px',
  formGap: '24px',
};

const baseClass = 'UISectionWrapper';

const UISectionWrapper: React.FC<UISectionWrapperProps> = ({
                                                             children,
                                                             id,
                                                             spacing = mockProps.spacing,
                                                             align = mockProps.align,
                                                             justify = mockProps.justify,
                                                             shadow = mockProps.shadow,
                                                             background = mockProps.background,
                                                             border = mockProps.border,
                                                             interactive = mockProps.interactive,
                                                             layout = mockProps.layout,
                                                             formPadding = mockProps.formPadding,
                                                             labelWidth = mockProps.labelWidth,
                                                             formGap = mockProps.formGap,
                                                             className = '',
                                                             onClick,
                                                             ...props
                                                           }) => {
  const containerClasses = [
    baseClass,
    // Spacing
    spacing !== 'default' && `${baseClass}--${spacing}`,
    // Alignment
    align !== 'center' && `${baseClass}--${align}`,
    // Justify
    justify !== 'center' && `${baseClass}--${justify}`,
    // Shadow
    shadow === 'none' && `${baseClass}--no-shadow`,
    shadow === 'light' && `${baseClass}--light-shadow`,
    shadow === 'medium' && `${baseClass}--medium-shadow`,
    shadow === 'strong' && `${baseClass}--strong-shadow`,
    // Background
    background === 'transparent' && `${baseClass}--transparent`,
    background === 'light' && `${baseClass}--light`,
    background === 'gray' && `${baseClass}--gray`,
    // Border
    border === 'default' && `${baseClass}--bordered`,
    border === 'primary' && `${baseClass}--primary-border`,
    // Interactive
    interactive === 'hoverable' && `${baseClass}--hoverable`,
    interactive === 'clickable' && `${baseClass}--clickable`,
    // Form layouts
    layout === 'form-horizontal' && `${baseClass}--form-horizontal`,
    layout === 'form-vertical' && `${baseClass}--form-vertical`,
    // Form padding
    formPadding !== 'medium' && `${baseClass}--form-padding-${formPadding}`,
    // Custom
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // CSS змінні для динамічних значень
  const cssVariables = {
    '--label-width': typeof labelWidth === 'number' ? `${labelWidth}px` : labelWidth,
    '--form-gap': typeof formGap === 'number' ? `${formGap}px` : formGap,
  } as React.CSSProperties;

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (interactive === 'clickable' && onClick) {
      onClick(event);
    }
  };

  return (
    <div
      id={id}
      className={containerClasses}
      style={cssVariables}
      onClick={handleClick}
      role={interactive === 'clickable' ? 'button' : undefined}
      tabIndex={interactive === 'clickable' ? 0 : undefined}
      {...props}
    >
      {children}
    </div>
  );
};

// Компоненти для структурованого контенту
export const UISectionHeader: React.FC<{ children: React.ReactNode; className?: string }> = ({
                                                                                               children,
                                                                                               className = '',
                                                                                             }) => (
  <div className={`${baseClass}__header ${className}`}>
    {children}
  </div>
);

export const UISectionBody: React.FC<{ children: React.ReactNode; className?: string }> = ({
                                                                                             children,
                                                                                             className = '',
                                                                                           }) => (
  <div className={`${baseClass}__body ${className}`}>
    {children}
  </div>
);

export const UISectionFooter: React.FC<{ children: React.ReactNode; className?: string }> = ({
                                                                                               children,
                                                                                               className = '',
                                                                                             }) => (
  <div className={`${baseClass}__footer ${className}`}>
    {children}
  </div>
);

// Нові компоненти для форм
export const UIFormRow: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = '' }) => (
  <div className={`${baseClass}__form-row ${className}`}>
    {children}
  </div>
);

export const UIFormLabels: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = '' }) => (
  <div className={`${baseClass}__form-labels ${className}`}>
    {children}
  </div>
);

export const UIFormFields: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = '' }) => (
  <div className={`${baseClass}__form-fields ${className}`}>
    {children}
  </div>
);

export const UIFormLabel: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = '' }) => (
  <div className={`${baseClass}__form-label ${className}`}>
    {children}
  </div>
);

export default UISectionWrapper;